// @flow
import * as React from 'react'
import styled from 'styled-components'
import { Container, config } from 'react-awesome-styled-grid'
import { getBackground } from '../helpers'
import { colors } from '@components/shared'
import SectionBackgroundImageParallax from './SectionBackgroundImageParallax'
import type { BackgroundType } from '../helpers'
import type { BackgroundColorType } from '../../../services/contentfulMarketingLandingPages'

export const BlockSection = (props: {
  sectionId: string,
  children: React.Node,
  backgroundColor: BackgroundColorType,
  backgroundImage?: string | null,
  isSingleContentBlock: boolean,
  isFirst: boolean
}): React.Node => {
  const sectionBackground: BackgroundType = getBackground(
    props.backgroundColor,
    props.backgroundImage
  )

  return (
    <Section
      backgroundColor={
        sectionBackground.backgroundImageUrl
          ? 'unset'
          : sectionBackground.backgroundColor
      }
      className={
        props.isSingleContentBlock
          ? 'single-content-block'
          : props.isFirst
            ? 'first-block'
            : ''
      }
    >
      <Container>
        <div className='main-section'>{props.children}</div>
      </Container>

      {sectionBackground.backgroundImageUrl &&
        sectionBackground.height &&
        sectionBackground.width && (
        <SectionBackgroundImageParallax
          uniqueId={props.sectionId}
          backgroundColor={sectionBackground.backgroundColor}
          imageUrl={sectionBackground.backgroundImageUrl}
          height={sectionBackground.height}
          width={sectionBackground.width}
        />
      )}
    </Section>
  )
}

const Section = styled.section`
  background-color: ${(props: any): string => props.backgroundColor};
  margin: 0;
  overflow: hidden;
  position: relative;
  padding: 50px 0;
  ${(props: any): string => config(props).media.md`
    padding: 80px 0;
  `}

  &.first-block {
    margin: -60px 0 0;
    padding: 125px 0 50px;
    ${(props: any): string => config(props).media.md`
      margin: -75px 0 0;
      padding: 155px 0 80px;
    `}
  }

  &.single-content-block {
    display: flex;
    align-items: center;
    margin: -60px 0 0;
    min-height: calc(100vh - 75px - 30px);
    ${(props: any): string => config(props).media.md`
      margin: -75px 0 0;
      min-height: calc(100vh - 75px - 60px - 30px);
    `}
    div.main-section {
      padding: 60px 0 40px;
    }
  }
`

export const RichTextContainer = styled.div`
  text-align: center;

  h1,
  h2,
  h3,
  h4 {
    font-family: 'Queens Condensed Light';
    font-weight: normal;
    line-height: 110%;
    padding: 0;

    margin: 0 0 14px;
    ${(props: any): string => config(props).media.md`
       margin: 0 0 18px;
    `}

    b {
      font-family: 'Queens Condensed Medium';
    }
    a {
      color: ${(props: any): string => props.linkColor || colors.tintOrange};
      :hover {
        opacity: 0.5;
      }
    }
  }

  h1 {
    font-size: 45px;
    ${(props: any): string => config(props).media.md`
      font-size: 60px;
    `}
  }
  h2 {
    font-size: 36px;
    ${(props: any): string => config(props).media.md`
      font-size: 45px;
    `}
  }
  h3 {
    font-size: 28px;
    ${(props: any): string => config(props).media.md`
      font-size: 36px;
    `}
  }
  h4 {
    font-size: 23px;
    ${(props: any): string => config(props).media.md`
      font-size: 28px;
    `}
  }

  h5,
  h6 {
    font-family: 'Good Sans Regular';
    font-weight: normal;
    line-height: 150%;
    padding: 0;

    margin: 0 0 14px;
    ${(props: any): string => config(props).media.md`
       margin: 0 0 18px;
    `}

    b {
      font-family: 'Good Sans Bold';
    }
    a {
      color: ${(props: any): string => props.linkColor || colors.tintOrange};
      :hover {
        opacity: 0.5;
      }
    }
  }
  h5 {
    font-size: 16px;
    ${(props: any): string => config(props).media.md`
      font-size: 18px;
    `}
  }
  h6 {
    font-size: 14px;
    ${(props: any): string => config(props).media.md`
      font-size: 16px;
    `}
  }

  img {
    max-width: 100%;
    margin: 0 0 24px;
  }

  p {
    font-family: 'Good Sans Regular';
    line-height: 150%;
    margin: 1.75em 0;
    padding: 0;
    text-align: left;

    font-size: 16px;
    ${(props: any): string => config(props).media.md`
      font-size: 18px;
    `}

    b {
      font-family: 'Good Sans Bold';
    }
    a {
      color: ${(props: any): string => props.linkColor || colors.tintOrange};
      :hover {
        opacity: 0.5;
      }
    }
  }

  hr {
    border-top: 0.5px solid
      ${(props: any): string => props.color || colors.sand};
    border-style: solid;
    width: 72px;

    margin: 38px auto;
    ${(props: any): string => config(props).media.md`
       margin: 44px auto;
    `}
  }
`
